import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useSettings, formatMoney } from "../lib/settings";
import { useAuth } from "../lib/auth";
import { useT, lbl } from "../lib/i18n";
import StatusBadge from "../components/StatusBadge";
import { Grid3x3, LayoutList, Plus, Filter, MessageCircle, FilePlus2 } from "lucide-react";
import ShareCarDialog from "../components/ShareCarDialog";

import { tr } from "../lib/i18n";
const STATUSES = [
  "Available", "Reserved", "Sold", "Under Preparation", "Under Maintenance",
  "Consignment", "Out for Inspection", "On Test Drive", "Returned to Owner",
];

export default function InventoryListPage() {
  const { settings } = useSettings();
  const { can, user } = useAuth();
  const [shareId, setShareId] = useState(null);
  const canShare = ["admin", "management", "sales"].includes(user?.role);
  const nav = useNavigate();
  const { t } = useT();
  const [view, setView] = useState("grid");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    ownership_type: "",
    make: "",
    year_from: "",
    year_to: "",
    price_from: "",
    price_to: "",
    published: "",
    sort: "-created_at",
    page: 1,
    limit: 24,
  });
  const [facets, setFacets] = useState({ makes: [], models: [] });

  useEffect(() => {
    api.get("/vehicles/facets").then(({ data }) => setFacets(data)).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== null));
        const { data } = await api.get("/vehicles", { params });
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(data.total / filters.limit));

  return (
    <div className="space-y-5" data-testid="inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow">{tr("x.vehicles_2")}</div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
            {tr("x.inventory")}
          </h1>
          <p className="text-sm text-[var(--gray-400)] mt-1">
            {data.total.toLocaleString()} {tr("x.vehicles_3")}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap w-full sm:w-auto">
          <div className="flex gap-1 bg-[hsl(var(--muted))] p-1 rounded-lg">
            <button
              data-testid="view-grid"
              onClick={() => setView("grid")}
              className={`w-8 h-8 rounded-md flex items-center justify-center ${view === "grid" ? "bg-[hsl(var(--card))] shadow-sm" : "text-[var(--gray-400)]"}`}
            >
              <Grid3x3 size={14} />
            </button>
            <button
              data-testid="view-table"
              onClick={() => setView("table")}
              className={`w-8 h-8 rounded-md flex items-center justify-center ${view === "table" ? "bg-[hsl(var(--card))] shadow-sm" : "text-[var(--gray-400)]"}`}
            >
              <LayoutList size={14} />
            </button>
          </div>
          {can("vehicles", "create") && (
            <>
            <button
              data-testid="add-vehicle-btn"
              onClick={() => nav("/inventory/new")}
              className="h-9 px-4 rounded-lg bg-accent-brand text-white font-semibold text-sm flex items-center justify-center gap-2 whitespace-nowrap inv-action"
            >
              <Plus size={14} /> <span>{tr("x.add_vehicle")}</span>
            </button>
            <button data-testid="new-owner-car-btn" onClick={() => nav("/forms/new/owner")}
              className="h-9 px-4 rounded-lg border text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 inv-action">
              <FilePlus2 size={14} /> <span className="sm:hidden">{t("sf.new_owner_short")}</span><span className="hidden sm:inline">{t("sf.new_owner")}</span>
            </button>
            </>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="panel p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <input
            data-testid="filter-q"
            placeholder={tr("x.search_stock_vin_plate")}
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          />
          <select
            data-testid="filter-status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          >
            <option value="">{tr("x.all_statuses")}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
          </select>
          <select
            data-testid="filter-ownership"
            value={filters.ownership_type}
            onChange={(e) => setFilters({ ...filters, ownership_type: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          >
            <option value="">{tr("x.all_ownership")}</option>
            <option value="Company Owned">{tr("x.company_owned")}</option>
            <option value="Customer Consignment">{tr("x.customer_consignment")}</option>
          </select>
          <select
            data-testid="filter-make"
            value={filters.make}
            onChange={(e) => setFilters({ ...filters, make: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          >
            <option value="">{tr("x.all_makes")}</option>
            {facets.makes.map((m) => <option key={m} value={m}>{lbl(m)}</option>)}
          </select>
          <input
            data-testid="filter-year-from"
            type="number"
            placeholder={tr("x.year_from")}
            value={filters.year_from}
            onChange={(e) => setFilters({ ...filters, year_from: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          />
          <select
            data-testid="filter-published"
            value={filters.published}
            onChange={(e) => setFilters({ ...filters, published: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          >
            <option value="">{tr("x.any_visibility")}</option>
            <option value="true">{tr("x.published")}</option>
            <option value="false">{tr("x.unpublished")}</option>
          </select>
          <input
            data-testid="filter-price-from"
            type="number"
            min="0"
            placeholder={tr("x.price_from")}
            value={filters.price_from}
            onChange={(e) => setFilters({ ...filters, price_from: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          />
          <input
            data-testid="filter-price-to"
            type="number"
            min="0"
            placeholder={tr("x.price_to")}
            value={filters.price_to}
            onChange={(e) => setFilters({ ...filters, price_to: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          />
          <select
            data-testid="filter-sort"
            value={filters.sort}
            onChange={(e) => setFilters({ ...filters, sort: e.target.value, page: 1 })}
            className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm"
          >
            <option value="-created_at">{tr("x.newest_first")}</option>
            <option value="asking_price">{tr("x.price_low_to_high")}</option>
            <option value="-asking_price">{tr("x.price_high_to_low")}</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-center py-8 text-[var(--gray-400)]">{tr("x.loading")}</div>}

      {!loading && data.items.length === 0 && (
        <div className="panel p-10 text-center text-[var(--gray-400)]">{tr("x.no_vehicles_match_the_filters")}</div>
      )}

      {view === "grid" && !loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.items.map((v) => (
            <Link
              key={v.id}
              to={`/inventory/${v.id}`}
              data-testid={`vehicle-card-${v.stock_number}`}
              className="panel panel-hover overflow-hidden"
            >
              <div className="aspect-[16/10] bg-[hsl(var(--muted))] relative overflow-hidden">
                {v.photos?.[0]?.url ? (
                  <img src={v.photos[0].variants?.thumbnail || v.photos[0].thumbnail_url || v.photos[0].url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--gray-400)] text-xs">{tr("x.no_photo")}</div>
                )}
                <div className="absolute top-2 start-2 flex gap-1.5 flex-wrap"><StatusBadge status={v.status} /></div>
                {v.publish_to_website && (
                  <div className="absolute top-2 end-2 pill" style={{ background: "var(--accent)", color: "white" }}>{tr("x.live")}</div>
                )}
              </div>
              <div className="p-4 space-y-1">
                <div className="mono text-xs text-[var(--gray-400)]">{v.stock_number}</div>
                <div className="font-semibold text-sm truncate">{v.make} {v.model} {v.model_year}</div>
                <div className="text-xs text-[var(--gray-400)]">
                  {v.mileage?.toLocaleString()} {tr("x.km_unit")}{v.specification ? ` · ${lbl(v.specification)}` : ""}
                </div>
                <div className="pt-1.5 flex items-center justify-between">
                  <div className="tabular font-semibold">{formatMoney(v.asking_price, settings)}</div>
                  <div className="text-[10px] text-[var(--gray-400)]">{v.days_in_stock}{tr("x.d_suffix")}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {view === "table" && !loading && (
        <div className="panel overflow-x-auto">
          <table className="w-full dense-table">
            <thead>
              <tr>
                <th>{tr("x.stock")}</th>
                <th>{tr("x.vehicle_2")}</th>
                <th>{tr("x.status")}</th>
                <th>{tr("x.ownership")}</th>
                <th>{tr("x.mileage")}</th>
                <th>{tr("x.asking")}</th>
                <th>{tr("x.days")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((v) => (
                <tr key={v.id} data-testid={`vehicle-row-${v.stock_number}`}>
                  <td className="mono">{v.stock_number}</td>
                  <td>
                    <div className="font-medium">{v.make} {v.model}</div>
                    <div className="text-xs text-[var(--gray-400)]">{v.model_year} · {v.vin?.slice(-6) || "—"}</div>
                  </td>
                  <td><StatusBadge status={v.status} /></td>
                  <td className="text-xs">{v.ownership_type}</td>
                  <td className="tabular">{v.mileage?.toLocaleString()}</td>
                  <td className="tabular font-semibold">{formatMoney(v.asking_price, settings)}</td>
                  <td className="tabular text-xs">{v.days_in_stock}</td>
                  <td className="whitespace-nowrap">
                    <Link to={`/inventory/${v.id}`} className="text-accent-brand text-xs font-semibold">{tr("x.open_2")}</Link>
                    {canShare && (
                      <button type="button" className="ms-3 align-middle text-emerald-600 hover:text-emerald-700" title={tr("share.whatsapp_short")}
                        aria-label={tr("share.whatsapp_short")} onClick={() => setShareId(v.id)} data-testid={`row-share-${v.stock_number}`}>
                        <MessageCircle size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {shareId && <ShareCarDialog vehicleId={shareId} open onClose={() => setShareId(null)} />}
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-[var(--gray-400)]">{tr("x.page")} {filters.page} {tr("x.of")} {totalPages}</div>
          <div className="flex gap-2">
            <button
              disabled={filters.page <= 1}
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              className="h-9 px-4 rounded-lg border border-[hsl(var(--border))] disabled:opacity-40"
            >{tr("x.previous")}</button>
            <button
              disabled={filters.page >= totalPages}
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              className="h-9 px-4 rounded-lg border border-[hsl(var(--border))] disabled:opacity-40"
            >{tr("x.next")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
